import { ConnectorMailIcon } from "@/components/connectors/providerIcons.tsx";
import { cardSurfaceClass, clickableCardClass } from "@/components/threads/shared.tsx";
import { AvatarWithFallback } from "@/components/ui/avatar.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Card } from "@/components/ui/card.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import type { ConnectorSummary } from "@/types/connectors.ts";
import { type Contact, contactTypeTag, type Handle } from "@/types/contact.ts";
import { Mail, Phone } from "lucide-react";

/** Pick the best handle to display: email preferred over phone, first one wins. */
const getPrimaryHandle = (handles: Handle[]): Handle | undefined => {
  const email = handles.find((h) => h.type === "emailAddress");
  if (email) return email;
  return handles[0];
};

export const ContactItem = ({
  contact,
  connector,
  isHovered,
  actions,
  interactive = false,
}: {
  contact: Contact;
  /** The mailbox this contact was synced from, when the caller has it resolved. */
  connector?: ConnectorSummary;
  isHovered?: boolean;
  actions?: React.ReactNode;
  /**
   * Clickable-card treatment: the thread-card hover reveal, for rows that open
   * the contact. Off by default so static uses (the merge dialog) stay plain.
   */
  interactive?: boolean;
}) => {
  const primaryHandle = getPrimaryHandle(contact?.handles ?? []);
  const typeTag = contactTypeTag(contact.type);
  const sourceLabel = contact.accountEmail ?? connector?.email ?? connector?.displayName;

  return (
    <Card
      className={cn(
        // Layout only. Surface, radius, and padding come from the shared card
        // classes below, so a card's shape is defined once, in one place.
        "flex flex-row w-full items-center gap-2 transition-all duration-200",
        interactive ? clickableCardClass : cardSurfaceClass,
      )}
    >
      <AvatarWithFallback
        size="lg"
        person={{
          name: contact.name || "?",
          picture: contact.picture,
          value: contact.id,
        }}
      />

      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm">{contact.name || "Unnamed"}</span>
        {primaryHandle && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground truncate mt-0.5">
            {primaryHandle.type === "phoneNumber" ? (
              <Phone className="size-3 shrink-0" />
            ) : (
              <Mail className="size-3 shrink-0" />
            )}
            {primaryHandle.value}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* The type badge gives way to the row actions on hover */}
        {!isHovered && typeTag && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
            {typeTag}
          </Badge>
        )}
        {isHovered && actions}
        {/* The icon carries the mailbox on its own, with the account a tooltip away — so
            it has to outlive the hover that opens it, and trails the row actions. */}
        {connector && (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Focusable so the tooltip is reachable without a pointer — a sighted
                  keyboard user has no other way to read the account. */}
              <span
                className="shrink-0 rounded-xs ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                tabIndex={0}
              >
                <ConnectorMailIcon provider={connector.provider} className="size-3.5" />
                {/* The tooltip is desktop-only, so this is the account's only reading
                    on touch and with a screen reader. */}
                {sourceLabel && <span className="sr-only">Synced from {sourceLabel}</span>}
              </span>
            </TooltipTrigger>
            {sourceLabel && <TooltipContent>Synced from {sourceLabel}</TooltipContent>}
          </Tooltip>
        )}
      </div>
    </Card>
  );
};
